<template>
  <div v-if="!isEdit" class="">
    <nav class="grid grid-cols-3 font-regular gap-4">
      <p
        v-if="!model?.Items || model.Items.length === 0"
        class="text-darkGray-2 col-span-full"
      >
        Här var det tomt. Lägg till minst ett fält för att kunna redigera det.
      </p>
      <p v-else class="text-darkGray-2 col-span-full">
        Här kan du redigera och ta bort fält i formuläret. Ta tag i ett fält för
        att flytta det.
      </p>
      <draggable
        @change="elementOrderChanged"
        v-if="isVisible && model?.Items && model.Items.length > 0"
        @start="drag = true"
        @end="drag = false"
        v-model="model.Items"
        group="people"
        class="col-span-full grid grid-cols-2 font-regular gap-4"
        :animation="200"
        :sort="true"
        item-key="element.item.id.name"
      >
        <template #item="{ element }">
          <div
            :tag="tag"
            href="#"
            class="flex gap-2 items-center justify-between px-6 py-3 bg-blue-4 text-sm rounded-lg cursor-move"
            :key="element.item.id"
          >
            <i
              class="text-darkGray-3 mr-2 text-[16px]"
              :class="getClassForElement(element.item.key)"
            ></i>
            <p class="mr-auto">{{ element.item.label }}</p>
            <div class="flex gap-2 text-darkGray-2 text-lg">
              <i
                v-if="element.item.label !== 'Villkor'"
                @click="editInput(element, $event)"
                title="Redigera"
                class="fas cursor-pointer fa-edit hover:text-blue-2"
                :isEditing="isEdit"
              ></i>
              <i
                v-if="element.item.label !== 'Villkor'"
                @click="removeInput(element.item)"
                title="Ta bort"
                class="fas cursor-pointer fa-trash-alt hover:text-red-2"
              ></i>
            </div>
          </div>
        </template>
      </draggable>
    </nav>
  </div>

  <div v-if="isEdit">
    <ElementEditor
      @remove="removeInput"
      @isEdit="isEdit = false"
      :model="editItem.item"
    ></ElementEditor>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch, markRaw } from "vue";
import { defineEmits, defineProps } from "@vue/runtime-core";
import { useStore } from "vuex";
import ElementEditorImport from "@/features/shared/components/form/ElementEditor.vue";
import ElementGroupEditorImport from "@/features/shared/components/form/ElementGroupEditor.vue";

// editView håller en komponentdefinition som tilldelas till en
// FormItemModel som lever i reactive store-state. markRaw hindrar
// Vue från att göra komponenten reactive.
const ElementEditor = markRaw(ElementEditorImport);
const ElementGroupEditor = markRaw(ElementGroupEditorImport);
import { FormBuilder2 } from "@/models/FormBuilder2";
import { FormItemModel } from "@/models/CheckboxGroup";
import { DynamicProperty, SelectedItems } from "../../models/dtos";
import { ToastNotification } from "@/models/ToastNotification";
import draggable from "vuedraggable";

const emit = defineEmits(["change", "remove", "select", "orderChange"]);
const elementOrderChanged = (e: any) => {
  if (model.value?.Items) {
    for (let i = 0; i < model.value.Items.length; i++) {
      model.value.Items[i].item.order = i;
    }
    emit("orderChange", model.value);
  }
};

const props = defineProps({
  selectedItems: {
    type: Object as () => SelectedItems,
    required: false,
  },
  // Deklareras endast för att undvika "Extraneous non-props
  // attributes"-varning när FormInputList renderas via dynamisk
  // <component :is>-binding i PositionFormBuilder + FormTemplateCreator
  // (fragment-root → attrs ärvs inte automatiskt). FormInputList
  // använder dem inte själv.
  positionType: {
    type: String,
    required: false,
  },
  positionTypeId: {
    type: Number,
    required: false,
  },
});
const store = useStore();
const tag = ref(0);
const isVisible = ref(true);

watch(store.getters.form, () => {
  tag.value++;

  isVisible.value = !isVisible.value;
  isVisible.value = !isVisible.value;
});

const drag = ref(false);

const model = computed(() => {
  return store.getters['formbuilder/form'] ? (store.getters['formbuilder/form'] as FormBuilder2) : null;
});

const iconMap: { [key: string]: string } = {
  form_ssn: "fa-brands fa-odnoklassniki",
  form_firstname: "fa-solid fa-signature",
  form_surname: "fa-solid fa-signature",
  form_address: "fa-regular fa-address-book",
  form_zipcode: "fa-regular fa-address-book",
  form_city: "fa-regular fa-address-book",
  form_email: "fa-regular fa-envelope",
  form_mobilePhone: "fa-solid fa-phone",
  form_homePhone: "fa-solid fa-phone",
  form_additional: "fa-solid fa-info",
  form_textarea_: "fa-solid fa-align-justify",
  form_text_: "fa-solid fa-font",
  customform_: "fa-regular fa-square-check",
  radio: "fa-regular fa-circle-dot",
  form_referer: "fa-regular fa-square-check",
  form_agreement: "fa-regular fa-handshake",
  form_file: "fa-solid fa-file-import",
  driverlicenseform: "fa-solid fa-id-card",
  licensetodriveform: "fa-solid fa-id-card",
  requirementsform: "fa-solid fa-square-check",
  summerpositionchoicesform: "fa-regular fa-pen-to-square",
  summerpositionperiodsform: "fa-regular fa-calendar-days",
  summerperiodsform: "fa-regular fa-calendar-days",
  form_linkedInUrl: "fa-brands fa-linkedin",
  template_standard: "fa-solid fa-star",
};

const getClassForElement = (key: any) => {
  const matchedKeys = Object.keys(iconMap).filter((mapKey) =>
    key.startsWith(mapKey)
  );
  return matchedKeys.length > 0 ? iconMap[matchedKeys[0]] : "";
};

const editItem = ref({} as FormItemModel);
const form = ref({} as FormBuilder2);
const isEdit = ref(false);
function editInput(item: FormItemModel, event: any) {
  if (item.id.name === "InputText2") item.editView = ElementEditor;
  if (item.id.name === "InputCheckbox2") item.editView = ElementEditor;
  if (item.id.name === "InputCheckboxGroup2") {
    item.editView = ElementGroupEditor;
  }

  editItem.value = item;

  isEdit.value = !isEdit.value;
  store.dispatch("setFormView", item);
  emit("change", item);
}

function onEnd(e: any) {
  model.value?.setOrder();
}

function onRemove(e: DynamicProperty) {
  if (model.value) {
    model.value.removeByProperty(e);
  }
}

function removeInput(item) {
  store.dispatch(
    "addNotification",
    new ToastNotification("success", "Fältet har tagits bort")
  );
  emit("select", item.key);
  if (model.value) {
    model.value.removeByProperty(item);
  }
}
</script>
<style>
.list-group-item:hover {
  cursor: pointer;
}
</style>
